import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  IonContent,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonLabel,
  IonPage,
  IonSegment,
  IonSegmentButton,
  useIonRouter,
} from '@ionic/react';
import type { InfiniteScrollCustomEvent } from '@ionic/core';
import { useQueryClient } from '@tanstack/react-query';
import { TopBar } from '../components/ui/TopBar';
import { useCollapsingTitle } from '../hooks/useCollapsingTitle';
import { PullToRefresh } from '../components/ui/PullToRefresh';
import { ListingHistoryRow } from '../components/listings/ListingHistoryRow';
import { useMyKitchenListings, useMyListingsCounts } from '../hooks/useListings';
import type { MyKitchenTab } from '../services/listingService';
import type { DishListing } from '../types/listing';

const TABS: readonly { key: MyKitchenTab; labelKey: string }[] = [
  { key: 'active', labelKey: 'kitchen.tabs.active' },
  { key: 'drafts', labelKey: 'kitchen.tabs.drafts' },
  { key: 'past', labelKey: 'kitchen.tabs.past' },
] as const;

/** `?tab=drafts` etc, validated against the real tab keys. Callers that know
 *  which bucket the user should see (e.g. Save Draft on the publish form) pass
 *  it so the count-based auto-select below doesn't send them somewhere else. */
function requestedTab(search: string): MyKitchenTab | null {
  const raw = new URLSearchParams(search).get('tab');
  return TABS.some((t) => t.key === raw) ? (raw as MyKitchenTab) : null;
}

/**
 * My Kitchen — the user's own listing history, split into Active / Drafts /
 * Past tabs. Each tab is a server-filtered, cursor-paginated infinite list.
 * Tab counts (fetched separately) pick the first non-empty tab on load. Each
 * row surfaces status, stock, dates, and order count (the last now comes back
 * on the listing itself, no per-listing fan-out).
 */
export const MyKitchenPage: React.FC = () => {
  const { t } = useTranslation('profile');
  const router = useIonRouter();
  const queryClient = useQueryClient();
  const { search } = useLocation();
  const [tab, setTab] = useState<MyKitchenTab>(() => requestedTab(search) ?? 'active');

  const countsQuery = useMyListingsCounts();
  const counts = countsQuery.data;
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useMyKitchenListings(tab);
  const listings = data?.pages.flat() ?? [];

  // Keep the tab on the first non-empty bucket (Active → Drafts → Past) until
  // the user deliberately picks one — so publishing/cancelling a listing moves
  // us to the right tab instead of stranding us on a stale one.
  //   (1) Only act on FRESH counts: `useMyListingsCounts` is stale-while-
  //       revalidate, so on mount it hands back the last cached counts and
  //       refetches. Gating on `!isFetching` waits for the refetch to settle,
  //       so we never latch onto an out-of-date bucket.
  //   (2) Re-evaluate whenever counts change (a new/cancelled listing shifts
  //       them) — but stop the moment the user taps a tab (`userPickedRef`).
  //   (3) An explicit `?tab=` counts as a deliberate pick: the caller already
  //       knows which bucket matters, so it sets the ref and the auto-select
  //       leaves it alone (a chef with active dishes would otherwise never see
  //       the draft they just saved). Applied in an effect as well as the
  //       initial state because Ionic keeps visited pages mounted in the router
  //       outlet — navigating here from the publish form when My Kitchen is
  //       already on the stack re-runs effects, not the state initialiser.
  const userPickedRef = useRef(requestedTab(search) !== null);
  useEffect(() => {
    const requested = requestedTab(search);
    if (!requested) return;
    userPickedRef.current = true;
    setTab(requested);
  }, [search]);
  useEffect(() => {
    if (userPickedRef.current) return;
    if (!counts || countsQuery.isFetching) return;
    const preferred: MyKitchenTab = counts.active
      ? 'active'
      : counts.drafts
        ? 'drafts'
        : 'past';
    setTab(preferred);
  }, [counts, countsQuery.isFetching]);

  const contentRef = useRef<HTMLIonContentElement>(null);
  // Prefix-invalidates every My Kitchen query (all tabs + counts). Stable
  // identity via useCallback — PullToRefresh rebinds its gesture when
  // `onRefresh` changes.
  const handleRefresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['listings', 'mine'] }),
    [queryClient],
  );

  const openListing = (listing: DishListing) =>
    router.push(
      listing.status === 'draft'
        ? `/publish-listing/${listing.id}`
        : `/listing/${listing.id}`,
    );

  // Title band hides on scroll-down, returns on scroll-up.
  const { collapsed, onScroll } = useCollapsingTitle(contentRef);

  return (
    <IonPage>
      <TopBar title={t('menu.kitchen')} titleCollapsed={collapsed} />
      <IonContent ref={contentRef} scrollEvents onIonScroll={onScroll}>
        <PullToRefresh contentRef={contentRef} onRefresh={handleRefresh}>
        <div className="mx-auto max-w-lg px-4 pt-4 pb-2 space-y-4">
          <IonSegment
            value={tab}
            onIonChange={(e) => {
              userPickedRef.current = true;
              setTab(e.detail.value as MyKitchenTab);
            }}
          >
            {TABS.map(({ key, labelKey }) => (
              <IonSegmentButton key={key} value={key}>
                <IonLabel>{t(labelKey)}</IonLabel>
              </IonSegmentButton>
            ))}
          </IonSegment>

          {listings.length > 0 ? (
            <div>
              {listings.map((listing) => (
                <ListingHistoryRow
                  key={listing.id}
                  listing={listing}
                  onClick={() => openListing(listing)}
                />
              ))}
            </div>
          ) : (
            <p className="py-10 text-center text-sm text-muted-foreground">
              {t(`kitchen.empty.${tab}`)}
            </p>
          )}
        </div>

        <IonInfiniteScroll
          disabled={!hasNextPage}
          onIonInfinite={async (ev: InfiniteScrollCustomEvent) => {
            if (hasNextPage && !isFetchingNextPage) {
              await fetchNextPage();
            }
            ev.target.complete();
          }}
        >
          <IonInfiniteScrollContent loadingSpinner="crescent" />
        </IonInfiniteScroll>
        </PullToRefresh>
      </IonContent>
    </IonPage>
  );
};
